import type { AgentModelSurface } from './agent-models';

export type ActiveMainSectionKey =
  | 'define'
  | 'design'
  | 'db-design'
  | 'repos'
  | 'tickets'
  | 'devops'
  | 'workspace'
  | 'users'
  | 'contact'
  | 'quotations';

export type HomeDashboardSectionKey = ActiveMainSectionKey | 'overview' | 'notifications';

export type HomeDashboardGroup = 'project' | 'organization';

export type ActiveMainSection = {
  key: ActiveMainSectionKey;
  title: string;
  icon: string;
  route: string;
  homeGroup: HomeDashboardGroup;
  dashboardTitle?: string;
  settingsSurfaceKey?: string;
};

export type SideNavItem = Pick<ActiveMainSection, 'title' | 'icon' | 'route'>;

export type HomeDashboardCard = {
  key: HomeDashboardSectionKey;
  title: string;
  route: string;
  group: HomeDashboardGroup;
  aliases?: string[];
};

const ACTIVE_MAIN_SECTIONS: ActiveMainSection[] = [
  {
    key: 'define',
    title: 'Define',
    icon: 'bi bi-pencil-square',
    route: '/define',
    homeGroup: 'project',
    settingsSurfaceKey: 'chat.define',
  },
  {
    key: 'design',
    title: 'Design',
    icon: 'bi bi-palette',
    route: '/design',
    homeGroup: 'project',
    settingsSurfaceKey: 'chat.design',
  },
  {
    key: 'db-design',
    title: 'DB Design',
    icon: 'bi bi-database',
    route: '/db-design',
    homeGroup: 'project',
    settingsSurfaceKey: 'chat.db-design',
  },
  {
    key: 'repos',
    title: 'Repos',
    icon: 'bi bi-github',
    route: '/repos',
    homeGroup: 'project',
    dashboardTitle: 'Repositories',
    settingsSurfaceKey: 'chat.repos',
  },
  {
    key: 'tickets',
    title: 'Tickets',
    icon: 'bi bi-ticket-perforated',
    route: '/tickets',
    homeGroup: 'project',
    settingsSurfaceKey: 'chat.tickets',
  },
  {
    key: 'devops',
    title: 'Dev-Ops',
    icon: 'bi bi-gear',
    route: '/devops',
    homeGroup: 'project',
    dashboardTitle: 'DevOps',
    settingsSurfaceKey: 'chat.devops',
  },
  {
    key: 'workspace',
    title: 'Workspace',
    icon: 'bi bi-collection',
    route: '/workspace',
    homeGroup: 'project',
  },
  {
    key: 'users',
    title: 'User Management',
    icon: 'bi bi-people',
    route: '/users',
    homeGroup: 'organization',
    dashboardTitle: 'Users',
  },
  {
    key: 'quotations',
    title: 'Quotations',
    icon: 'bi bi-file-earmark-text',
    route: '/quotations',
    homeGroup: 'project',
    settingsSurfaceKey: 'chat.quotations',
  },
  // {
  //   key: 'whatsapp',
  //   title: 'Whatsapp',
  //   icon: 'bi bi-whatsapp',
  //   route: '/whatsapp',
  //   homeGroup: 'project',
  //   settingsSurfaceKey: 'chat.whatsapp',
  // },
];

export const ACTIVE_MAIN_NAV_ITEMS: SideNavItem[] = ACTIVE_MAIN_SECTIONS
  .filter((section) => section.key !== 'design')
  .map(({ title, icon, route }) => ({
    title,
    icon,
    route,
  }));

export const HOME_DASHBOARD_CARDS: HomeDashboardCard[] = [
  {
    key: 'overview',
    title: 'Overview',
    route: '/project',
    group: 'project',
    aliases: ['summary'],
  },
  ...ACTIVE_MAIN_SECTIONS.filter((section) => section.key !== 'design').map((section) => ({
    key: section.key,
    title: section.dashboardTitle ?? section.title,
    route: section.route,
    group: section.homeGroup,
    aliases: [section.title],
  })),
  {
    key: 'notifications',
    title: 'Notifications',
    route: '/notifications',
    group: 'organization',
  },
];

export function filterAgentModelSurfacesForActiveNav(
  surfaces: AgentModelSurface[]
): AgentModelSurface[] {
  const allowedKeys = new Set(
    ACTIVE_MAIN_SECTIONS.map((section) => section.settingsSurfaceKey).filter(
      (value): value is string => Boolean(value)
    )
  );
  return surfaces.filter((surface) => allowedKeys.has(surface.key));
}

function normalizeSectionToken(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function findHomeDashboardCard(
  titleOrKey: string | null | undefined
): HomeDashboardCard | null {
  const normalized = normalizeSectionToken(titleOrKey);
  if (!normalized) return null;
  return (
    HOME_DASHBOARD_CARDS.find((card) => {
      const candidates = [card.key, card.title, ...(card.aliases ?? [])];
      return candidates.some((candidate) => normalizeSectionToken(candidate) === normalized);
    }) ?? null
  );
}
